import TomSelect from "tom-select";
import { selectDropdownLayerConfig } from "./selectDropdownLayer";
import { getJson } from "../api";
import { escapeHtml } from "../common";
import { startSelectLoading } from "./selectLoading";

function getOptionPresentationMap(selectEl) {
  const map = new Map();

  selectEl.querySelectorAll("option").forEach((optionEl) => {
    if (!(optionEl instanceof HTMLOptionElement)) return;

    const value = String(optionEl.value ?? "");
    const optionClass = String(optionEl.dataset.optionClass ?? "").trim();
    if (!value || !optionClass) return;

    map.set(value, { optionClass });
  });

  return map;
}

function buildColorOptionMarkup(label, optionClass, variant) {
  const safeLabel = escapeHtml(label);
  if (!optionClass) {
    return `<div>${safeLabel}</div>`;
  }

  return `<div class="ts-color-option ts-color-option--${variant} ${optionClass}"><span class="ts-color-option__label">${safeLabel}</span></div>`;
}

function getEmptyOptionLabel(selectEl) {
  const emptyOption = selectEl.querySelector('option[value=""]');
  return emptyOption ? String(emptyOption.textContent ?? "").trim() : "";
}

function buildCommonConfig(selectEl, { dropdownInput = true } = {}) {
  const optionPresentationMap = getOptionPresentationMap(selectEl);
  const hasColorizedOptions = optionPresentationMap.size > 0;

  const config = {
    ...selectDropdownLayerConfig,
    create: false,
    allowEmptyOption: selectEl.dataset.emptyOptionPlaceholder !== "1",
    placeholder: selectEl.dataset.placeholder ?? getEmptyOptionLabel(selectEl),
    render: {
      no_results: function () {
        return '<div class="no-results">Brak wyników</div>';
      },
      loading: function () {
        return '<div class="loading text-center py-2"><span class="spinner-border spinner-border-sm text-primary" role="status" aria-label="Ładowanie"></span></div>';
      },
      loading_more: function () {
        return '<div class="loading-more-results text-center py-2"><span class="spinner-border spinner-border-sm text-primary" role="status" aria-label="Ładowanie kolejnych wyników"></span></div>';
      },
      no_more_results: function () {
        return null;
      },
    },
  };

  if (hasColorizedOptions) {
    const getOptionClass = (data) => {
      const value = String(data?.value ?? data?.id ?? "");
      return optionPresentationMap.get(value)?.optionClass ?? "";
    };

    config.render.option = function (data) {
      return buildColorOptionMarkup(String(data?.text ?? ""), getOptionClass(data), "dropdown");
    };

    config.render.item = function (data) {
      return buildColorOptionMarkup(String(data?.text ?? ""), getOptionClass(data), "item");
    };
  }

  if (dropdownInput) {
    config.plugins = { dropdown_input: {} };
  }

  return config;
}

function buildAjaxConfig(selectEl) {
  const ajaxUrl = selectEl.dataset.ajaxUrl ?? "";
  if (!ajaxUrl) {
    return {};
  }

  const virtualScrollEnabled = (selectEl.dataset.virtualScroll ?? "1") !== "0";
  const searchParam = selectEl.dataset.ajaxSearchParam ?? "q";
  const limitParam = selectEl.dataset.ajaxLimitParam ?? "limit";
  const pageParam = selectEl.dataset.ajaxPageParam ?? "page";
  const perPageParam = selectEl.dataset.ajaxPerPageParam ?? limitParam;
  const pageStart = Math.max(parseInt(selectEl.dataset.ajaxPageStart ?? "1", 10) || 1, 1);
  const maxOptions = Math.min(Math.max(parseInt(selectEl.dataset.maxOptions ?? "20", 10) || 20, 1), 100);

  const buildRequestUrl = (query, page = pageStart) => {
    const requestUrl = new URL(ajaxUrl, window.location.origin);
    requestUrl.searchParams.set(searchParam, query);
    requestUrl.searchParams.set(limitParam, String(maxOptions));
    requestUrl.searchParams.set(perPageParam, String(maxOptions));
    requestUrl.searchParams.set(pageParam, String(page));
    return requestUrl;
  };

  const mapPayloadToResults = (payload) =>
    Array.isArray(payload?.results)
      ? payload.results
      : Array.isArray(payload?.data)
        ? payload.data.map((item) => ({
            id: String(item.id ?? ""),
            text: item.text ?? item.nazwa ?? `Szkoła #${item.id ?? ""}`,
          }))
        : [];

  const resolveHasMore = (payload, page, perPage) => {
    const pagination = payload?.pagination ?? null;
    const pages = Number(pagination?.pages);
    if (Number.isFinite(pages) && pages > 0) {
      return page < pages;
    }

    const total = Number(pagination?.total);
    if (Number.isFinite(total) && total >= 0) {
      return page * perPage < total;
    }

    if (typeof payload?.has_more === "boolean") {
      return payload.has_more;
    }

    if (typeof pagination?.has_more === "boolean") {
      return pagination.has_more;
    }

    // Without explicit pagination metadata, avoid guessing to prevent duplicate pages.
    return false;
  };

  return {
    valueField: "id",
    labelField: "text",
    searchField: ["text"],
    preload: true,
    loadThrottle: 300,
    maxOptions,
    ...(virtualScrollEnabled ? { plugins: { virtual_scroll: {} } } : {}),
    ...(virtualScrollEnabled
      ? {
          firstUrl(query) {
            return buildRequestUrl(query, pageStart).toString();
          },
        }
      : {}),
    shouldLoad(query) {
      return query.length === 0 || query.trim().length >= 2;
    },
    load: async function (query, callback) {
      const finishLoading = startSelectLoading(selectEl);
      let requestUrl = buildRequestUrl(query, pageStart);

      if (virtualScrollEnabled && typeof this?.getUrl === "function") {
        const requestedUrl = this.getUrl(query);
        if (requestedUrl) {
          requestUrl = new URL(requestedUrl, window.location.origin);
        }
      }

      const currentPage = Math.max(
        parseInt(requestUrl.searchParams.get(pageParam) ?? String(pageStart), 10) || pageStart,
        pageStart,
      );

      try {
        const payload = await getJson(requestUrl.toString());
        if (query !== this.inputValue()) {
          callback(Object.values(this.options));
          return;
        }
        const results = mapPayloadToResults(payload);

        if (virtualScrollEnabled && typeof this?.setNextUrl === "function") {
          const hasMore = resolveHasMore(payload, currentPage, maxOptions);
          const nextUrl = hasMore ? buildRequestUrl(query, currentPage + 1).toString() : false;
          this.setNextUrl(query, nextUrl);
        }

        callback(results);
      } catch {
        if (query !== this.inputValue()) {
          callback(Object.values(this.options));
          return;
        }
        if (virtualScrollEnabled && typeof this?.setNextUrl === "function") {
          this.setNextUrl(query, false);
        }

        callback([]);
      } finally {
        finishLoading();
      }
    },
  };
}

function isAlreadyEnhanced(selectEl) {
  if (!selectEl) return false;

  // TomSelect sets this property on the original element.
  if (selectEl.tomselect) return true;

  // When a row is cloned, dataset flags can be copied, but the JS instance is not.
  // The wrapper is a better indicator that TomSelect already enhanced this element.
  const hasWrapper = Boolean(selectEl.closest?.(".ts-wrapper"));
  if (hasWrapper) return true;

  return false;
}

export function initSpnSelect2(root = document) {
  const scope = root instanceof Element ? root : document;

  scope.querySelectorAll("[data-spn-table] select").forEach((selectEl) => {
    if (!(selectEl instanceof HTMLSelectElement)) return;
    if (selectEl.disabled) return;
    // Don't enhance the hidden template row; we clone it for new rows.
    if (selectEl.closest("[data-row-template]")) return;
    if (isAlreadyEnhanced(selectEl)) return;

    // Mark before init to avoid double-init on fast reflows
    selectEl.dataset.tomselectInit = "1";

    new TomSelect(selectEl, buildCommonConfig(selectEl));
  });
}

export function initSelect2(rootOrSelector = ".js-select", selector = null) {
  const scope = rootOrSelector instanceof Element ? rootOrSelector : document;
  const query =
    typeof selector === "string" && selector !== ""
      ? selector
      : typeof rootOrSelector === "string" && rootOrSelector !== ""
        ? rootOrSelector
        : ".js-select";

  try {
    scope.querySelectorAll(query).forEach((selectEl) => {
      if (!(selectEl instanceof HTMLSelectElement)) return;
      if (selectEl.disabled) return;
      if (isAlreadyEnhanced(selectEl)) return;

      const action = selectEl.dataset.onSelect ?? false;
      const form = selectEl.closest("form");

      selectEl.dataset.tomselectInit = "1";

      // Always use dropdown_input so the search field lives inside the dropdown,
      // not inline in the control area. Without it, the control renders both the
      // selected item chip and the search input stacked vertically, which expands
      // the control height on focus and breaks the filter row layout.
      //
      // Empty filter options are disabled placeholders, not selectable values.
      // Active filters are cleared with their filter chips above the table.
      const commonConfig = buildCommonConfig(selectEl, { dropdownInput: true });
      const ajaxConfig = buildAjaxConfig(selectEl);
      const mergedPlugins = {
        ...(commonConfig.plugins ?? {}),
        ...(ajaxConfig.plugins ?? {}),
      };

      let select2 = new TomSelect(selectEl, {
        ...commonConfig,
        ...ajaxConfig,
        ...(Object.keys(mergedPlugins).length > 0 ? { plugins: mergedPlugins } : {}),
      });

      select2.on("change", (value) => {
        if (selectEl.dataset.readonlySelectPreview === "1") return;
        if (action === "redirect") {
          if (form) {
            if (typeof form.requestSubmit === "function") {
              form.requestSubmit();
            } else {
              form.dispatchEvent(new Event("submit", { bubbles: true, cancelable: true }));
            }
          }
          // const url = new URL(window.location.href);
          // url.searchParams.set("spn_id", value);
          // window.location.href = url.toString();
        }
      });
    });
  } catch (error) {
    console.log(error);
  }
}
